import React from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
  type ChipProps,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import type { ChangeStatus, RecordChange, Row } from './submissionDiff';

const COLUMN_LABELS: Record<string, string> = {
  room_asset: 'Room',
  deal_offer: 'Deal option',
  asset: 'Inclusion asset',
  currencyisocode: 'Currency',
  ac_deal_category__c: 'Deal category',
  ac_deal_sub_category__c: 'Offer country',
  expiry_date_travel_from__c: 'Travel from',
  room_nights_forecast__c: 'Room nights forecast',
  salesperson_gross_sales_forecast__c: 'TTV forecast',
  salesperson_margin_forecast__c: 'Gross margin forecast',
  price__c: 'Sell price',
  cost_price_gst__c: 'Net rate',
  number_of_nights__c: 'LOS',
  nightly_sell_price__c: 'Extra night sell',
  nightly_cost_price__c: 'Extra night net',
  rrp__c: 'RRP',
  ancillary_deal_option__c: 'Experience deal option',
  applies_to_specific_days__c: 'Specific days',
  day_of_the_week__c: 'Days of week',
  do_these_surcharges_accumulate__c: 'Accumulates',
  from_age__c: 'Age from',
  to_age__c: 'Age to',
  other_extra_bedding_type__c: 'Other bedding type',
  applies_to__c: 'Rate type',
  sell_price_aud__c: 'Sell price (AUD)',
  commission__c: 'Commission %',
};

const STATUS_CHIPS: Record<ChangeStatus, { label: string; color: ChipProps['color'] }> = {
  added: { label: 'Added', color: 'success' },
  changed: { label: 'Changed', color: 'warning' },
  deleted: { label: 'Deleted', color: 'error' },
  unchanged: { label: 'Same', color: 'default' },
};

const formatCell = (value: Row[string] | undefined) =>
  value === null || value === undefined || String(value).trim() === '' ? '-' : String(value);

const columnLabel = (column: string) => {
  const tidied = column.replace(/__c$/, '').replace(/_/g, ' ');
  return COLUMN_LABELS[column] ?? tidied.charAt(0).toUpperCase() + tidied.slice(1);
};

interface Props {
  label: string;
  changes: Array<RecordChange>;
}

export default function SubmissionSection({ label, changes: rows }: Props) {
  const columns = Array.from(new Set(rows.flatMap(({ row }) => Object.keys(row))));
  const summary = (Object.keys(STATUS_CHIPS) as Array<ChangeStatus>)
    .map((status) => [status, rows.filter((row) => row.status === status).length] as const)
    .filter(([, count]) => count > 0)
    .map(([status, count]) => `${count} ${STATUS_CHIPS[status].label.toLowerCase()}`)
    .join(' · ');

  return (
    <Accordion disableGutters disabled={rows.length === 0}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="body2" sx={{ flexGrow: 1 }}>
          {label}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
          {summary || 0}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Change</TableCell>
              {columns.map((column) => (
                <TableCell key={column} sx={{ whiteSpace: 'nowrap', fontWeight: 600 }} title={column}>
                  {columnLabel(column)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(({ status, row, previousRow, changedColumns }, index) => (
              <TableRow key={index}>
                <TableCell>
                  <Chip size="small" {...STATUS_CHIPS[status]} />
                </TableCell>
                {columns.map((column) => {
                  const isChanged = changedColumns.includes(column);
                  return (
                    <TableCell
                      key={column}
                      sx={{
                        whiteSpace: 'nowrap',
                        color: status === 'deleted' ? 'text.disabled' : undefined,
                        textDecoration: status === 'deleted' ? 'line-through' : undefined,
                        fontWeight: isChanged ? 600 : undefined,
                      }}
                    >
                      {formatCell(row[column])}
                      {isChanged && (
                        <Typography component="div" variant="caption" color="text.secondary">
                          was {formatCell(previousRow?.[column])}
                        </Typography>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </AccordionDetails>
    </Accordion>
  );
}
